
import { Options, Vue } from 'vue-class-component';
import { Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';
import { UploadIcon, TruckIcon, CheckIcon, PlusIcon, RefreshIcon } from '@heroicons/vue/solid';
import ContractTable from '@/components/App/ContractTable.vue';
import ReportTable from '@/components/App/ReportTable.vue';
import AppButton from '@/components/Common/AppButton.vue';
import { endOfMonth, format } from 'date-fns';

import { processResponseErrors } from '@/helpers/errors';

import FileSaver from 'file-saver';
import FileUpload, { VueUploadItem } from 'vue-upload-component';

import { useApolloClient, useQuery, useMutation } from '@vue/apollo-composable';
import { Contract, GetCsvMonthReportDocument, GetMonthsDocument, Month, ImportCsvContractTimeRecordsDocument, GetCsvContractTimeRecordsDocument, ValidateCsvContractsTimeRecordsDocument, GetCsvReportAffectedContractsDocument, ImportCsvMultipleContractsTimeRecordsDocument } from '@/graphql/generated-operations';
import { useToast } from 'vue-toastification';

export interface ModalSettings {
  type: 'month' | 'date';
  contract: Contract;
  period: string;
  selectedMonth?: Month;
  selectedDate?: string;
}

@Options({
  components: {
    AppButton,
    ContractTable,
    ReportTable,
    Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot,
    UploadIcon, TruckIcon, CheckIcon, PlusIcon, RefreshIcon,
    FileUpload
  },
})
export default class Reports extends Vue {

  months: Month[] = [];
  selectedMonth: Month | null = null;

  reportModalIsOpen = false;
  modalSettings?: ModalSettings;
  modalRefreshKey = 0;

  csvModalIsOpen = false;
  csvImportFile: VueUploadItem | null = null;

  multipleContractsCsvModalIsOpen = false;
  verifyContratsModalIsOpen = false;
  importInProgress = false;

  affectedContracts: Contract[] = [];

  reportTableRefreshKey = 0;

  loading = true;

  created() {
    const { onResult: onResult } = useQuery(GetMonthsDocument);

    onResult(queryResult => {
      this.months = queryResult.data.getMonths;

      const currentMonth = format(new Date(), 'y-MM');
      this.months.map((month, index) => {
        if ( month.key == currentMonth) {
          this.selectedMonth = this.months[index];
        }
      });

      this.loading = false;
    });
  }

  openContractMonthModal(contract: Contract) {
    this.modalSettings = {
      type: 'month',
      contract: contract,
      period: this.selectedMonth!.name,
      selectedMonth: this.selectedMonth!,
    };

    this.reportModalIsOpen = true;
  }

  openContractDateModal(contract: Contract, date: string) {
    const d = new Date(date);
    this.modalSettings = {
      type: 'date',
      contract: contract,
      period: format(d, 'EEEE d.M.y'),
      selectedDate: format(d, 'y-MM-dd'),
    };

    this.reportModalIsOpen = true;
  }

  closeReportModal() {
    this.reportModalIsOpen = false;
  }

  async submitModal() {
    const reportTable = this.$refs.reportTable as ReportTable;
    const result = await reportTable.submitData();
    if ( result ) {
      useToast().success('Report has been saved.');
      this.reportModalIsOpen = false;
      this.modalRefreshKey++;
    }
  }

  async exportToCsv() {
    const query = await useApolloClient().client.query({
      query: GetCsvMonthReportDocument,
      variables: {
        month: this.selectedMonth!.key,
      }
    });

    const {name, content} = query.data.getCsvMonthReport;

    const blob = new Blob([content], {type: 'text/csv'});
    FileSaver.saveAs(blob, name);
  }

  openMultipleContractsImportModal() {
    this.multipleContractsCsvModalIsOpen = true;
  }

  closeMultipleContractsImportModal() {
    this.multipleContractsCsvModalIsOpen = false;
    this.csvImportFile = null;
  }

  openVerifyContratsModal() {
    this.verifyContratsModalIsOpen = true;
  }

  closeVerifyContratsModal() {
    this.verifyContratsModalIsOpen = false;
  }

  async prepareMultipleContractsFromCsv() {
    this.importInProgress = true;

    if ( !this.csvImportFile || !this.csvImportFile.file ) {
      useToast().error('Please select valid CSV file.');
      this.importInProgress = false;
      return;
    }
    const data = this.csvImportFile.file;

    const validateQuery = await useApolloClient().client.query({
      query: ValidateCsvContractsTimeRecordsDocument,
      variables: {
        month: this.selectedMonth!.key,
        content: await data.text(),
      }
    });

    const isSuccess = processResponseErrors(validateQuery?.data?.validateCsvContractsTimeRecords);
    if ( !isSuccess ) {
      this.csvImportFile = null;
      this.importInProgress = false;
      return;
    }

    const affectedContractsQuery = await useApolloClient().client.query({
      query: GetCsvReportAffectedContractsDocument,
      variables: {
        content: await data.text(),
      }
    });

    this.affectedContracts = affectedContractsQuery.data?.getCsvReportAffectedContracts;
    this.importInProgress = false;
    this.verifyContratsModalIsOpen = true;
  }

  async importMultipleContractsFromCsv()
  {
    this.importInProgress = true;

    if ( !this.csvImportFile || !this.csvImportFile.file ) {
      useToast().error('Please select valid CSV file.');
      this.importInProgress = false;
      return;
    }
    const data = this.csvImportFile.file;

    const monthStart = new Date(`${this.selectedMonth!.key}-01`);
    const { mutate: importCsvMultipleContractsTimeRecords } = useMutation(ImportCsvMultipleContractsTimeRecordsDocument);
    const result = await importCsvMultipleContractsTimeRecords({
      dateFrom: format(monthStart, 'yyyy-MM-dd'),
      dateTo: format(endOfMonth(monthStart), 'yyyy-MM-dd'),
      content: await data.text(),
    });

    const isSuccess = processResponseErrors(result?.data?.importCsvMultipleContractsTimeRecords);
    if ( !isSuccess ) {
      this.csvImportFile = null;
      this.importInProgress = false;
      return;
    }

    useToast().success('CSV file has been successfully imported.');
    this.affectedContracts = [];
    this.multipleContractsCsvModalIsOpen = false;
    this.verifyContratsModalIsOpen = false;
    this.csvImportFile = null;
    this.importInProgress = false;
    this.modalRefreshKey++;
    this.reportTableRefreshKey++;
    setTimeout(() => {
      // Headless UI bug workaround
      document.documentElement.style.overflow = '';
    }, 1000);
  }

  openImportModal() {
    this.csvModalIsOpen = true;
  }

  closeImportModal() {
    this.csvModalIsOpen = false;
  }

  onInputFile(inputFile: VueUploadItem | undefined) {
    if ( !inputFile || inputFile.file === undefined ) return;

    this.csvImportFile = inputFile;
  }

  async importContractCsvFile() {
    if ( !this.csvImportFile || !this.csvImportFile.file ) {
      useToast().error('Please select valid CSV file.');
      return;
    }

    const data = this.csvImportFile.file;

    const { mutate: saveMonthTimeRecords } = useMutation(ImportCsvContractTimeRecordsDocument);
    const result = await saveMonthTimeRecords({
      contractID: this.modalSettings!.contract.id,
      month: this.selectedMonth!.key,
      content: await data.text(),
    });

    const isSuccess = processResponseErrors(result?.data?.importCsvContractTimeRecords);
    if ( !isSuccess ) {
      this.csvImportFile = null;
      return;
    }

    useToast().success('CSV file has been successfully imported.');
    this.reportTableRefreshKey++;
    this.modalRefreshKey++;
    this.csvModalIsOpen = false;
    this.csvImportFile = null;
  }

  async exportContractToCsv() {
    const query = await useApolloClient().client.query({
      query: GetCsvContractTimeRecordsDocument,
      variables: {
        contractID: this.modalSettings!.contract.id,
        month: this.selectedMonth!.key,
      }
    });

    const {name, content} = query.data.getCsvContractTimeRecords;

    const blob = new Blob([content], {type: 'text/csv'});
    FileSaver.saveAs(blob, name);
  }

}
